"use client";

import { memo, useMemo } from "react";
import dompurify from "dompurify";
import { parseDocument } from "htmlparser2";

import { cleanQuillHtml, stripColorAndBackgroundFromStyle } from "@/utils/clean-quill-html";

interface ContentReaderProps {
  htmlContent: string;
  className?: string;
}

const ALLOWED_TAGS = new Set([
  "h1", "h2", "h3", "h4", "h5", "h6",
  "p", "div", "span", "strong", "em", "u", "s",
  "sub", "sup", "blockquote", "ul", "ol", "li",
  "a", "img", "code", "pre", "br"
]);

const ALLOWED_ATTRS = new Set([
  "href", "target", "rel", "src", "alt", "width", "height", "class", "style"
]);

/** Quill emits &nbsp;, <br>, ZWSP, and whitespace-only “empty” blocks — treat as no content. */
function isEffectivelyEmptyInnerHtml(innerHtml: string): boolean {
  const stripped = innerHtml
    .replace(/<br\s*\/?>/gi, "")
    .replace(/&nbsp;|&#160;|&#xA0;/gi, "")
    .replace(/\u00a0/g, "")
    .replace(/\u200b/g, "")
    .replace(/\s+/g, "");
  return stripped.length === 0;
}

const EMPTY_PARAGRAPH_RE = /<p\b[^>]*>(?:\s|&nbsp;|&#160;|&#xA0;|\u00a0|<br\s*\/?>)*<\/p>/gi;
const EMPTY_LIST_ITEM_RE = /<li\b[^>]*>(?:\s|&nbsp;|&#160;|&#xA0;|\u00a0|<br\s*\/?>)*<\/li>/gi;
const EMPTY_OL_RE = /<ol\b[^>]*>\s*<\/ol>/gi;
const EMPTY_UL_RE = /<ul\b[^>]*>\s*<\/ul>/gi;

/** Strip Quill filler blocks and trim so prose margins aren’t multiplied by empty nodes. */
function normalizeQuillReaderHtml(html: string): string {
  let prev = "";
  let out = html.trim();
  while (out !== prev) {
    prev = out;
    out = out
      .replace(EMPTY_PARAGRAPH_RE, "")
      .replace(EMPTY_LIST_ITEM_RE, "")
      .replace(EMPTY_OL_RE, "")
      .replace(EMPTY_UL_RE, "")
      .trim();
  }
  return out;
}

function transformNode(node: any): string {
  if (node.type === "text") return node.data || "";

  if (node.type === "tag") {
    const name = node.name;
    // If tag is not allowed, unwrap it (return children only)
    if (!ALLOWED_TAGS.has(name)) {
      return (node.children || []).map(transformNode).join("");
    }

    const attribs = node.attribs || {};
    const children = (node.children || []).map(transformNode).join("");

    // Drop Quill “blank line” paragraphs ( <p><br></p>, &nbsp;, multiple breaks )
    if (name === "p" && isEffectivelyEmptyInnerHtml(children)) {
      return "";
    }

    // Quill sometimes wraps blank lines in a bare div
    if (name === "div" && isEffectivelyEmptyInnerHtml(children)) {
      return "";
    }

    // Remove empty list rows (Shift+Enter quirks / pasted HTML)
    if (name === "li" && isEffectivelyEmptyInnerHtml(children)) {
      return "";
    }

    const classAttr = attribs.class || "";
    const styleAttr = attribs.style || "";

    // --- Special Transformations (Quill Classes) ---

    // Headers
    const headerMatch = classAttr.match(/ql-header-(\d)/);
    if (name === "p" && headerMatch) {
      const level = headerMatch[1];
      return `<h${level} class="font-bold text-blue-600 dark:text-blue-400">${children}</h${level}>`;
    }

    // Blockquote
    if (name === "p" && classAttr.includes("ql-blockquote")) {
      return `<blockquote class="border-l-4 pl-4 italic text-gray-600 dark:text-gray-300">${children}</blockquote>`;
    }

    // Alignment
    const alignMatch = classAttr.match(/ql-align-(center|right|justify)/);
    if (alignMatch) {
      return `<${name} class="text-${alignMatch[1]}">${children}</${name}>`;
    }

    // Font size
    const sizeMatch = classAttr.match(/ql-size-(small|large|huge)/);
    if (sizeMatch) {
      const sizeMap: Record<string, string> = {
        small: "text-sm",
        large: "text-lg",
        huge: "text-2xl",
      };
      return `<span class="${sizeMap[sizeMatch[1]]}">${children}</span>`;
    }

    // --- Attribute Cleaning ---
    // Never re-apply Quill light-theme color / background; prose + dark mode own contrast.
    const cleanedStyleAttr = stripColorAndBackgroundFromStyle(styleAttr);

    // Links: Enforce security and styling
    if (name === "a") {
      const href = attribs.href || "#";
      // Basic javascript protocol check (sanitizer will also catch this, but good for normalizer)
      const safeHref = href.trim().toLowerCase().startsWith("javascript:") ? "#" : href;
      return `<a href="${safeHref}" target="_blank" rel="noopener noreferrer" class="break-all text-blue-600 underline hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300">${children}</a>`;
    }

    // Images
    if (name === "img") {
      const src = attribs.src || "";
      const alt = attribs.alt || "";
      const width = attribs.width || "";
      const height = attribs.height || "";
       // Allow base64 and http(s)
      return `<img src="${src}" alt="${alt}" width="${width}" height="${height}" class="h-auto max-w-full rounded-lg" />`;
    }

    // Generic Tag Construction
    // Remove Quill specific classes, keep others if needed (though we mostly replace them above)
    // We already handled special classes. For the rest, we whitelist attributes.
    
    // Construct attributes string
    const cleanAttribs = Object.entries(attribs)
      .filter(([k, v]: any) => {
        if (!ALLOWED_ATTRS.has(k)) return false;
        if (k === "class" && v.includes("ql-")) return false; // Remove internal quill classes
        if (k === "style") return false; // We handled style manually above
        return true;
      })
      .map(([k, v]: any) => `${k}="${v}"`)
      .join(" ");

    const styleEscaped = cleanedStyleAttr
      .replace(/&/g, "&amp;")
      .replace(/"/g, "&quot;")
      .replace(/</g, "&lt;");
    const styleString = styleEscaped ? ` style="${styleEscaped}"` : "";
    const extraClasses = ""; // Add default classes if needed for specific tags

    return `<${name} ${cleanAttribs}${styleString}>${children}</${name}>`;
  }

  return "";
}

function convertQuillToSemantic(html: string): string {
  if (!html) return "";
  const dom = parseDocument(html);
  const transformed = dom.children.map(transformNode).join("");
  return normalizeQuillReaderHtml(transformed);
}

// Initialize sanitizer only on client to avoid "window is not defined" or factory errors
const sanitizer = typeof window !== "undefined" ? dompurify(window) : undefined;

export const ContentReader = memo(function ContentReader({
  htmlContent,
  className = "",
}: ContentReaderProps) {
  
  // Memoize the transformation to avoid re-parsing on every render if content doesn't change
  const finalHtml = useMemo(() => {
    const fixed = convertQuillToSemantic(cleanQuillHtml(htmlContent ?? ""));
    // Double-sanitize with DOMPurify on client if available, strictly for security
    // On server, 'fixed' is used (which is already normalized and attribute-whitelisted by transformNode)
    if (sanitizer) {
      const cleaned = sanitizer.sanitize(fixed, {
        ALLOWED_TAGS: Array.from(ALLOWED_TAGS),
        ALLOWED_ATTR: Array.from(ALLOWED_ATTRS),
      });
      // Sanitizer can leave empty wrappers; run spacing pass again after strip
      return normalizeQuillReaderHtml(cleaned);
    }
    return fixed;
  }, [htmlContent]);

  if (!finalHtml) {
    return null;
  }

  return (
    <>
      <style jsx>{`
        [data-content-reader] {
          min-width: 0;
          max-width: 100%;
          overflow-wrap: break-word;
          word-wrap: break-word;
        }
        [data-content-reader] :global(p),
        [data-content-reader] :global(li),
        [data-content-reader] :global(blockquote) {
          overflow-wrap: break-word;
          word-break: break-word;
        }
        /* Quill nests <p> inside <li>; keep list rhythm tight vs. body paragraphs */
        [data-content-reader] :global(li > p) {
          margin-top: 0.25em;
          margin-bottom: 0.25em;
        }
        [data-content-reader] :global(li > p:first-child) {
          margin-top: 0;
        }
        [data-content-reader] :global(li > p:last-child) {
          margin-bottom: 0;
        }
        [data-content-reader] :global(pre) {
          max-width: 100%;
          overflow-x: auto;
          white-space: pre;
          word-break: normal;
        }
        [data-content-reader] :global(code) {
          overflow-wrap: break-word;
          word-break: break-word;
        }
        [data-content-reader] :global(h1),
        [data-content-reader] :global(h2),
        [data-content-reader] :global(h3),
        [data-content-reader] :global(h4),
        [data-content-reader] :global(h5),
        [data-content-reader] :global(h6) {
          font-weight: bold;
          color: #2563eb;
          overflow-wrap: break-word;
          word-break: break-word;
        }
        :global(.dark) [data-content-reader] :global(h1),
        :global(.dark) [data-content-reader] :global(h2),
        :global(.dark) [data-content-reader] :global(h3),
        :global(.dark) [data-content-reader] :global(h4),
        :global(.dark) [data-content-reader] :global(h5),
        :global(.dark) [data-content-reader] :global(h6) {
          color: #60a5fa;
        }
        [data-content-reader] :global(a) {
          color: #2563eb;
          text-decoration: underline;
        }
        [data-content-reader] :global(a:hover) {
          color: #1e40af;
        }
        :global(.dark) [data-content-reader] :global(a) {
          color: #60a5fa;
        }
        :global(.dark) [data-content-reader] :global(a:hover) {
          color: #93c5fd;
        }
        [data-content-reader] :global(p.text-center) {
          text-align: center;
        }
        [data-content-reader] :global(p.text-right) {
          text-align: right;
        }
        [data-content-reader] :global(p.text-justify) {
          text-align: justify;
        }
      `}</style>
      <div
        data-content-reader
        className={`prose prose-blue prose-base prose-img:max-w-full prose-img:my-5 prose-img:rounded-xl prose-headings:text-blue-600 prose-p:my-2 prose-blockquote:my-4 prose-ul:my-3 prose-ol:my-3 prose-li:my-0.5 dark:prose-invert dark:prose-headings:text-blue-400 prose-pre:max-w-full prose-pre:overflow-x-auto prose-hr:my-6 max-w-none break-words text-gray-700 dark:text-gray-100 ${className}`}
        dangerouslySetInnerHTML={{ __html: finalHtml }}
        suppressHydrationWarning={true}
      />
    </>
  );
});

ContentReader.displayName = "ContentReader";

